/** 推送分析只读 API 与报表日历边界换算；消息/设备筛选按契约分别解释。 */
import type {
  PushAnalyticsListQuery,
  PushAnalyticsNotification,
  PushAnalyticsNotificationDetail,
  PushAnalyticsQuery,
  PushAnalyticsResponse,
} from '@novel-admin/shared';
import { apiRequest, apiRequestOffsetPaged } from '~/api/client';
import { downloadAuthedCsv } from '~/api/download-authed-csv';

const BASE = '/api/v1/push/analytics';
const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');
export const pushAnalyticsApi = {
  overview: (query: PushAnalyticsQuery) =>
    apiRequest<PushAnalyticsResponse>({ method: 'GET', path: BASE, query }),
  notifications: (query: PushAnalyticsListQuery) =>
    apiRequestOffsetPaged<PushAnalyticsNotification>({
      method: 'GET',
      path: `${BASE}/notifications`,
      query,
    }),
  detail: (id: string, appId?: string) =>
    apiRequest<PushAnalyticsNotificationDetail>({
      method: 'GET',
      path: `${BASE}/notifications/${encodeURIComponent(id)}`,
      query: { app_id: appId },
    }),
  export: (query: PushAnalyticsQuery) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query))
      if (value !== undefined) params.set(key, String(value));
    return downloadAuthedCsv(`${API_BASE}${BASE}/export?${params}`, 'push-analytics.csv');
  },
};

function calendarParts(
  date: Date,
  timezone: string,
): [number, number, number, number, number, number] {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  return ['year', 'month', 'day', 'hour', 'minute', 'second'].map((key) =>
    Number(parts.find((part) => part.type === key)?.value),
  ) as [number, number, number, number, number, number];
}

/** 选择器给本地日历日；仅取年月日，在报表时区求真实零点，不以浏览器时区充当报表时区。 */
export function reportDayBoundary(date: Date, timezone: string): Date {
  const target: [number, number, number, number, number, number] = [
    date.getFullYear(),
    date.getMonth() + 1,
    date.getDate(),
    0,
    0,
    0,
  ];
  const wall = Date.UTC(target[0], target[1] - 1, target[2]);
  const candidates = new Set<number>();
  // 夏令时切换前后分别取 offset，重复零点取最早时刻；不存在的零点显式拒绝。
  for (const shift of [-36, -12, 0, 12, 36]) {
    const sample = wall + shift * 3_600_000;
    const parts = calendarParts(new Date(sample), timezone);
    const offset =
      Date.UTC(parts[0], parts[1] - 1, parts[2], parts[3], parts[4], parts[5]) - sample;
    candidates.add(wall - offset);
  }
  const matches = [...candidates]
    .filter((candidate) =>
      calendarParts(new Date(candidate), timezone).every((part, index) => part === target[index]),
    )
    .sort((left, right) => left - right);
  const firstMatch = matches[0];
  if (firstMatch === undefined)
    throw new Error('所选日期在统计时区不存在午夜，请调整日期或统计时区');
  return new Date(firstMatch);
}

export function buildPushAnalyticsWindow(range: [Date, Date], timezone: string, now: Date) {
  const nextDay = new Date(range[1]);
  nextDay.setDate(nextDay.getDate() + 1);
  return {
    start_at: reportDayBoundary(range[0], timezone).toISOString(),
    end_at: new Date(
      Math.min(reportDayBoundary(nextDay, timezone).getTime(), now.getTime()),
    ).toISOString(),
  };
}

/** 报表时区的今天，以 Date 仅承载日历年月日供共享选择器计算快捷范围。 */
export function pushAnalyticsCalendarToday(timezone: string, now = new Date()): Date {
  const parts = calendarParts(now, timezone);
  return new Date(parts[0], parts[1] - 1, parts[2]);
}

export function defaultPushAnalyticsRange(now = new Date()): [Date, Date] {
  const parts = calendarParts(now, 'UTC');
  const end = new Date(parts[0], parts[1] - 1, parts[2]);
  const start = new Date(end);
  start.setDate(start.getDate() - 6);
  return [start, end];
}
